import { useDebouncedCallback } from "@evergis/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getAppConfig } from "../../../ducks/appConfig";
import { getUsername } from "../../../ducks/user";
import { getUsersAndRoles } from "../../../resources/security";
import { useValueChanged } from "../EditResource/hooks/useValueChanged";
import { getAclRole } from "./getAclRole";
import { AccessRoleGroup, ChipDataUser } from "./types";

const DEBOUNCE_TIME = 500;

// eslint-disable-next-line max-lines-per-function
export function useUsersAndRoles(filterCurrentUser = true) {
  const { settings } = useSelector(getAppConfig);
  const currentUser: string = useSelector(getUsername);

  const [filter, setFilter] = useState<string>("");
  const [pickedUsers, setPickedUsers] = useState<string[]>([]);

  const {
    data: usersAndRoles,
    loading: usersAndRolesLoading,
    refetch,
  } = getUsersAndRoles.use({
    input: { filter },
    immediate: false,
  });

  const debounce = useDebouncedCallback(DEBOUNCE_TIME);

  useEffect(() => {
    debounce(() => {
      refetch();
    });
  }, [filter, refetch]);

  const users = useMemo(() => {
    if (!usersAndRoles) {
      return [];
    }

    let newUsers: ChipDataUser[] = settings?.isRole ? usersAndRoles : usersAndRoles.filter(value => !value.isRole);

    newUsers = filterCurrentUser ? newUsers.filter(({ aclRole }) => aclRole !== `__${currentUser}`) : newUsers;

    return [
      {
        name: AccessRoleGroup.Public,
        isSecondary: true,
        icon: "public_group",
        aclRole: "__public",
      },
      ...newUsers,
    ];
  }, [usersAndRoles, settings?.isRole, currentUser, filterCurrentUser]);

  const suggestRef = useRef<HTMLElement>();

  const filterChanged = useValueChanged(filter);

  useEffect(() => {
    // open suggest menu on users change
    if (filterChanged) {
      suggestRef.current?.blur();
      suggestRef.current?.focus();
    }
  }, [users, filterChanged]);

  const allUsers = useMemo(() => {
    const userNames = users.map(({ name }) => name);

    return [
      ...users,
      ...pickedUsers
        .filter(roleName => !userNames.includes(roleName))
        .map(roleName => ({ name: roleName, aclRole: getAclRole(roleName) })),
    ];
  }, [users]);

  return {
    users: allUsers,
    usersAndRolesLoading,
    filter,
    setFilter,
    autoCompleteProps: {
      value: filter,
      onChange: setFilter,
      onGetInnerRef: input => (suggestRef.current = input),
    },
    pickedUsers,
    setPickedUsers,
  };
}
